import { createWebHashHistory, createRouter, RouteRecordRaw } from "vue-router";

import PatientsListPage from "@/pages/Dashboard/Patient/PatientListPage.vue";
import PatientPage from "@/pages/Dashboard/Patient/PatientPage.vue";
import PatientFormPage from "@/pages/Dashboard/Patient/PatientFormPage.vue";
import CreatePatientPage from "@/pages/Dashboard/Patient/CreatePatientPage.vue";
import CaregiversPage from "@/pages/Dashboard/Patient/Caregivers/CaregiversPage.vue";
import CaregiverFormPage from "@/pages/Dashboard/Patient/Caregivers/CaregiverFormPage.vue";
import AddCaregiverPage from "@/pages/Dashboard/Patient/Caregivers/AddCaregiverPage.vue";
import LoginPage from "@/pages/Authentication/LoginPage.vue";
import ForgotPasswordPage from "@/pages/Authentication/ForgotPasswordPage.vue";
import ResetPasswordPage from "@/pages/Authentication/ResetPasswordPage.vue";
import EmailConfirmationPage from "@/pages/Authentication/EmailConfirmationPage.vue";
import ConsentGrantPage from "@/pages/Dashboard/Consent/ConsentGrantPage.vue";
import ConsentsPage from "@/pages/Dashboard/Consent/ConsentsPage.vue";
import CreateConsentPage from "@/pages/Dashboard/Consent/CreateConsentPage.vue";
import UpdateConsentPage from "@/pages/Dashboard/Consent/UpdateConsentPage.vue";
import ConsentVersionPage from "@/pages/Dashboard/Consent/ConsentVersionPage.vue";
import HomePage from "@/pages/Dashboard/HomePage.vue";
import SystemUsersPage from "@/pages/System/Users/SystemUsersPage.vue";
import CreateUserPage from "@/pages/System/Users/CreateUserPage.vue";
import UpdateUserPage from "@/pages/System/Users/UpdateUserPage.vue";
import UserPage from "@/pages/System/Users/UserPage.vue";
import CentersPage from "@/pages/System/Centers/CentersPage.vue";
import CreateCenterPage from "@/pages/System/Centers/CreateCenterPage.vue";
import CenterPage from "@/pages/System/Centers/CenterPage.vue";
import RolesPage from "@/pages/System/Roles/RolesPage.vue";
import CreateRolePage from "@/pages/System/Roles/CreateRolePage.vue";
import UpdateRolePage from "@/pages/System/Roles/UpdateRolePage.vue";
import UpdateCenterPage from "@/pages/System/Centers/UpdateCenterPage.vue";
import WelcomePage from "@/pages/Dashboard/WelcomePage.vue";
import ProfilePage from "@/pages/Dashboard/Profile/ProfilePage.vue";
import UpdatePasswordPage from "@/pages/Dashboard/Profile/UpdatePasswordPage.vue";
import ContentCategoriesPage from "@/pages/Dashboard/Contents/Categories/ContentCategoriesPage.vue";
import ContentCategoryPage from "@/pages/Dashboard/Contents/Categories/ContentCategoryPage.vue";
import CreateContentCategoryPage from "@/pages/Dashboard/Contents/Categories/CreateContentCategoryPage.vue";
import UpdateContentCategoryPage from "@/pages/Dashboard/Contents/Categories/UpdateContentCategoryPage.vue";
import CreateContentPage from "@/pages/Dashboard/Contents/CreateContentPage.vue";
import UpdateContentPage from "@/pages/Dashboard/Contents/UpdateContentPage.vue";
import MessagesPage from "@/pages/Dashboard/Messages/MessagesPage.vue";
import MessagesEmpty from "@/components/FK/Messages/MessagesEmpty.vue";
import MessagesBody from "@/components/FK/Messages/MessagesBody.vue";
import LoadingSpinner from "@/components/Widgets/Spinner.vue";

import stateStore from "@/utils/store";

import { Role } from "@/utils/models/user.model";
// RESOLVERS
import { patientResolver } from "@/router/resolvers/patient.resolver";
import { patientExtraDefinitionsResolver } from "@/router/resolvers/patient_extra.resolver";
import { logoutResolver } from "@/router/resolvers/logout.resolver";
import { userResolver } from "@/router/resolvers/user.resolver";
import { centerResolver } from "@/router/resolvers/center.resolver";
import { contentCategoryResolver } from "./resolvers/content_category.resolver";
import { contentResolver } from "./resolvers/content.resolver";
import { consentResolver } from "./resolvers/consent.resolver";
import { consentVersionResolver } from "./resolvers/consent_version.resolver";
import { consentVersionRestoreResolver } from "./resolvers/consent_version_restore.resolver";
import { surveyResolver } from "./resolvers/survey.resolver";
import { userConsentsResolver } from "./resolvers/user_consents.resolver";
import { extraDefinitionResolver } from "./resolvers/extra_definition.resolver";
import { therapyTrendResolver } from "./resolvers/therapy_trend.resolver";

import SurveyPage from "@/pages/Dashboard/Surveys/SurveyPage.vue";
import config from "@/utils/const/config";
import TheraphyTrendPage from "@/pages/Dashboard/Therapy/TheraphyTrendPage.vue";
import RequestSupportPage from "@/pages/Authentication/RequestSupportPage.vue";
import AuthRequestSupportPage from "@/pages/Dashboard/Profile/RequestSupportPage.vue";
import SurveyListPage from "@/pages/Dashboard/Surveys/SurveyListPage.vue";
import CreateSurveyPage from "@/pages/Dashboard/Surveys/CreateSurveyPage.vue";
import UpdateSurveyPage from "@/pages/Dashboard/Surveys/UpdateSurveyPage.vue";
import { rolesResolver } from "./resolvers/roles.resolver";
import { userWithRolesResolver } from "./resolvers/user_with_roles.resolver";
import InviteUserPage from "@/pages/System/Users/InviteUserPage.vue";
import InvitationPage from "@/pages/System/Users/InvitationPage.vue";
import { invitationResolver } from "./resolvers/invitation.resolver";
import ZonesPage from "@/pages/System/Zones/ZonesPage.vue";
import CreateZonePage from "@/pages/System/Zones/CreateZonePage.vue";
import UpdateZonePage from "@/pages/System/Zones/UpdateZonePage.vue";
import { zoneResolver } from "./resolvers/zone.resolver";
import { roleWithZonesResolver } from "./resolvers/role_with_zones.resolver";
import { centerWithZonesResolver } from "./resolvers/center_with_zones.resolver";
import { zonesResolver } from "./resolvers/zones.resolver";
import ZonesHCPPage from "@/pages/System/Zones/ZonesHCPPage.vue";
import { zoneHCPResolver } from "./resolvers/zone_hcp.resolver";
import RightToErasurePage from "@/pages/Dashboard/Profile/RightToErasurePage.vue";
import PoliciesPage from "@/pages/Dashboard/Profile/PoliciesPage.vue";
import PolicyPage from "@/pages/Dashboard/Profile/PolicyPage.vue";
import { userConsentResolver } from "./resolvers/user_consent.resolver";
import OTPLoginVerivyPage from "@/pages/Authentication/OTPLoginVerivyPage.vue";
import OTPInvitationPage from "@/pages/System/Users/OTPInvitationPage.vue";
import OTPInvitationVerifyPage from "@/pages/System/Users/OTPInvitationVerifyPage.vue";

const routes = [
  {
    path: "/login",
    name: "Login",
    component: LoginPage,
  },
  {
    path: "/login/verify",
    name: "LoginVerify",
    component: OTPLoginVerivyPage,
  },
  {
    path: "/logout",
    name: "Logout",
    component: LoadingSpinner,
    beforeEnter: logoutResolver,
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPasswordPage,
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPasswordPage,
  },
  {
    path: "/email-confirmation",
    name: "EmailConfirmation",
    component: EmailConfirmationPage,
  },
  {
    path: "/request-support",
    name: "RequestSupport",
    component: RequestSupportPage,
  },
  {
    path: "/user-consents",
    name: "UserConsents",
    component: ConsentGrantPage,
    beforeEnter: userConsentsResolver,
    meta: {
      auth: true,
    },
  },
  {
    path: "/invitations",
    name: "Invitation",
    beforeEnter: invitationResolver,
  },
  {
    path: "/invitations/:token",
    name: "InvitationForm",
    beforeEnter: invitationResolver,
    component: InvitationPage,
  },
  {
    path: "/invitations-otp/:token",
    name: "InvitationOTPForm",
    beforeEnter: invitationResolver,
    component: OTPInvitationPage,
  },
  {
    path: "/invitations-otp/:token/verify",
    name: "InvitationOTPVerifyForm",
    beforeEnter: invitationResolver,
    component: OTPInvitationVerifyPage,
  },
  /* {
    path: "/cookie-policy",
    name: "PrivacyCookiePolicy",
    component: PrivacyCookiePolicy,
  }, */
  {
    path: "/",
    name: "Home",
    component: HomePage,
    meta: {
      auth: true,
    },
    children: [
      {
        path: "/",
        name: "Welcome",
        component: WelcomePage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/patients",
        name: "Patients",
        component: PatientsListPage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/patient/:id",
        name: "PatientProfile",
        component: PatientPage,
        beforeEnter: patientResolver,
        meta: {
          auth: true,
        }
      },
      {
        path: "/patient/:id/edit",
        name: "PatientProfileEdit",
        component: PatientFormPage,
        beforeEnter: patientExtraDefinitionsResolver,
        meta: {
          auth: true,
        }
      },
      {
        path: "/patient/:id/caregivers",
        name: "PatientCaregivers",
        component: CaregiversPage,
        beforeEnter: patientResolver,
        meta: {
          auth: true,
        }
      },
      {
        path: "/patient/:id/caregivers/add",
        name: "PatientAddCaregiver",
        component: AddCaregiverPage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/patient/:id/caregivers/:caregiver_id/edit",
        name: "PatientEditCaregiver",
        beforeEnter: patientResolver,
        component: CaregiverFormPage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/surveys",
        name: "Surveys",
        component: SurveyListPage,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/create-survey",
        name: "CreateSurvey",
        component: CreateSurveyPage,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/survey/:id/edit",
        name: "UpdateSurvey",
        component: UpdateSurveyPage,
        beforeEnter: surveyResolver,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/patient/:id/surveys/:survey_id",
        name: "Survey",
        component: SurveyPage,
        beforeEnter: surveyResolver,
        meta: {
          auth: true,
        }
      },
      {
        path: "/create-patient",
        name: "CreatePatient",
        component: CreatePatientPage,
        beforeEnter: extraDefinitionResolver,
        meta: {
          auth: true,
        }
      },
      {
        path: "/system-users",
        name: "SystemUsers",
        component: SystemUsersPage,
        beforeEnter: rolesResolver,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/create-user",
        name: "CreateUser",
        component: CreateUserPage,
        beforeEnter: rolesResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/invite-user",
        name: "InviteUser",
        component: InviteUserPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/user/:id",
        name: "UserProfile",
        component: UserPage,
        beforeEnter: userResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/user/:id/edit",
        name: "UserProfileEdit",
        component: UpdateUserPage,
        beforeEnter: userWithRolesResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/system-centers",
        name: "Centers",
        component: CentersPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/create-center",
        name: "CreateCenter",
        component: CreateCenterPage,
        beforeEnter: zonesResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/center/:id",
        name: "Center",
        component: CenterPage,
        beforeEnter: centerResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/center/:id/edit",
        name: "CenterEdit",
        component: UpdateCenterPage,
        beforeEnter: centerWithZonesResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
      {
        path: "/system-roles",
        name: "Roles",
        component: RolesPage,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/create-role",
        name: "CreateRole",
        component: CreateRolePage,
        beforeEnter: zonesResolver,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/role/:id/edit",
        name: "RoleEdit",
        component: UpdateRolePage,
        beforeEnter: roleWithZonesResolver,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/profile",
        name: "Profile",
        component: ProfilePage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/profile/update-password",
        name: "UpdatePassword",
        component: UpdatePasswordPage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/profile/request-support",
        name: "AuthRequestSupport",
        component: AuthRequestSupportPage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/profile/policies",
        name: "Policies",
        component: PoliciesPage,
        meta: {
          auth: true,
        }
      },
      {
        path: "/profile/policies/:id",
        name: "Policy",
        component: PolicyPage,
        beforeEnter: userConsentResolver,
        meta: {
          auth: true,
        }
      },
      {
        path: "/profile/right-to-erasure",
        name: "RightToErasure",
        component: RightToErasurePage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/contents",
        name: "ContentCategories",
        component: ContentCategoriesPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/content-category/:id",
        name: "ContentCategory",
        component: ContentCategoryPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/content-category/:id/edit",
        name: "ContentCategoryEdit",
        component: UpdateContentCategoryPage,
        beforeEnter: contentCategoryResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/content-category/:id/create-content",
        name: "CreateContent",
        component: CreateContentPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/content/:id/edit",
        name: "UpdateContent",
        component: UpdateContentPage,
        beforeEnter: contentResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/create-content-category",
        name: "CreateContentCategory",
        component: CreateContentCategoryPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal]
        }
      },
      {
        path: "/consents",
        name: "Consents",
        component: ConsentsPage,
        meta: {
          auth: true,
          enabled: config.feature.consents
        },
      },
      {
        path: "/create-consent",
        name: "CreateConsent",
        component: CreateConsentPage,
        meta: {
          auth: true,
          enabled: config.feature.consents
        },
      },
      {
        path: "/consent/:id",
        name: "UpdateConsent",
        component: UpdateConsentPage,
        beforeEnter: consentResolver,
        meta: {
          auth: true,
          enabled: config.feature.consents
        },
      },
      {
        path: "/consent/:id/version/:version_id",
        name: "ConsentVersion",
        component: ConsentVersionPage,
        beforeEnter: consentVersionResolver,
        meta: {
          auth: true,
          enabled: config.feature.consents
        },
      },
      {
        path: "/consent/:id/version/:version_id/restore",
        name: "RestoreConsentVersion",
        component: UpdateConsentPage,
        beforeEnter: consentVersionRestoreResolver,
        meta: {
          auth: true,
          enabled: config.feature.consents
        },
      },
      {
        path: "/messages",
        name: "Messages",
        component: MessagesPage,
        meta: {
          auth: true,
          roles: [Role.admin, Role.hcp, Role.internal],
          enabled: false,
        },
        children: [
          {
            path: "",
            name: "MessageEmpty",
            component: MessagesEmpty
          },
          {
            path: ":id",
            name: "MessageBody",
            component: MessagesBody,
          },
        ]
      },
      {
        path: "/patient/:id/therapy/:therapy_id/trend/",
        name: "TherapyStatistics",
        component: TheraphyTrendPage,
        beforeEnter: therapyTrendResolver,
        meta: {
          auth: true,
        },
      },
      {
        path: "/patient/:id/therapy/trend",
        name: "TherapiesStatistics",
        component: TheraphyTrendPage,
        beforeEnter: therapyTrendResolver,
        meta: {
          auth: true,
        },
      },
      {
        path: "/zones",
        name: "Zones",
        component: ZonesPage,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/create-zone",
        name: "CreateZone",
        component: CreateZonePage,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      /* {
        path: "/zone/:id",
        name: "Zone",
        component: ZonePage,
        beforeEnter: zoneResolver,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      }, */
      {
        path: "/zone/:id/edit",
        name: "ZoneEdit",
        component: UpdateZonePage,
        beforeEnter: zoneResolver,
        meta: {
          auth: true,
          roles: [Role.admin],
        }
      },
      {
        path: "/zone-hcp-list",
        name: "ZoneHCPs",
        component: ZonesHCPPage,
        // beforeEnter: zoneHCPResolver,
        meta: {
          auth: true,
          roles: [Role.admin, Role.internal],
        }
      },
    ]
  },
] as RouteRecordRaw[];

const router = createRouter({
  history: createWebHashHistory(),
  linkExactActiveClass: "active",
  routes: routes,
  scrollBehavior() {
    return { top: 0, left: 0, behavior: "smooth" };
  },
});

router.beforeEach(async (to, from) => {
  if (to.name === 'Login' && stateStore.isAuthenticated()) {
    return { name: 'Welcome' }
  }
  if (to.meta.auth === true && !stateStore.isAuthenticated()) {
    console.debug('NOT_AUTHENTICATED')
    return { name: 'Login' }
  }
  if (to.meta.enabled === false) {
    return { name: 'Welcome' }
  }
  if (to.meta.roles !== undefined) {
    if (!(to.meta.roles as Role[]).includes(stateStore.user?.info.role.type!)) {
      return { name: 'Welcome' }
    }
  }
  if (to.meta.auth === true && stateStore.isAuthenticated()) {
    console.debug('IS_AUTHENTICATED');
    if (stateStore.isPasswordExpired() && to.name !== 'UpdatePassword' && !config.feature.otp_login) {
      return { name: 'UpdatePassword' }
    }
  }
})

export default router;